export enum InvoiceDeliveryStatus {
  Draft = 'draft',
  Sending = 'sending',
  Delivered = 'delivered',
  Failed = 'failed',
}

export interface InvoiceMetadata {
  invoiceNumber?: string
  invoiceDate?: string | number
  eligibleEmployees?: number
  planCosts?: number
  agencyFee?: number
  platformFee?: number
  totalDue?: number
  /** OE1 only: part 2 of the Open Enrollment split, the OE2 balance. */
  balanceDue?: number
  /** OE1 only: the whole Open Enrollment amount, both parts together. */
  oeTotalDue?: number
}

/** Excel cell the parser couldn't convert (e.g. "N/A" in Total Due). */
export interface ParseWarning {
  field: string
  label: string
  /** A1-style cell address, e.g. "B14". Empty when the row is absent. */
  cellAddress: string
  rawValue: string
  reason: string
  /** invalid = cell has unparseable value; missing = cell or row is empty. */
  kind: 'invalid' | 'missing'
}

/**
 * Which email an invoice is sent with. Each template is its own invoice for a
 * client and month, so a January OE1 and OE2 stay live side by side.
 */
export enum InvoiceTemplate {
  MONTHLY = 'MONTHLY',
  INITIAL_OE = 'INITIAL_OE',
  OE_FIRST = 'OE_FIRST',
  OE_FINAL = 'OE_FINAL',
}

/** How the client settles this invoice, as returned on each delivery. */
export enum InvoicePaymentType {
  WIRE = 'WIRE',
  STRIPE = 'STRIPE',
}

/** The bank account backing a `us_bank_account`-type Stripe payment method. */
export interface StripeUsBankAccountDto {
  bank_name?: string | null
  last4?: string | null
}

/** Stripe PaymentMethod object on the customer, trimmed to the fields we use. */
export interface StripePaymentMethodDto {
  id?: string
  type?: string
  us_bank_account?: StripeUsBankAccountDto | null
}

/** Who triggered a Stripe webhook event — a person, when known. */
export interface StripePaymentEventTriggeredByDto {
  id: string
  name: string
}

/** Stripe's `last_payment_error` on a failed PaymentIntent, trimmed to the fields we use. */
export interface StripeLastPaymentErrorDto {
  /** Stripe error code, e.g. "insufficient_funds". */
  code?: string | null
  message?: string | null
}

/** A single Stripe webhook event recorded against a draw attempt. */
export interface StripePaymentEventDto {
  eventId: string
  /** Stripe event type, e.g. "payment_intent.succeeded". */
  eventType: string
  stripeAccount?: string | null
  /** When Stripe generated the event. */
  eventCreatedAt: string
  /** When our webhook handler received it. */
  receivedAt?: string | null
  triggeredBy?: StripePaymentEventTriggeredByDto | null
  /** Set on `payment_intent.payment_failed` events. */
  lastPaymentError?: StripeLastPaymentErrorDto | null
}

/** A single draw attempt against the invoice, as recorded in Stripe. */
export interface InvoicePaymentAttemptDto {
  /** Stripe PaymentIntent id for this attempt, e.g. "pi_3UBXN5AQK7wHi6bl1GErHafC". */
  stripePaymentIntentId?: string | null
  /** Stripe customer id the draw was made against, e.g. "cus_VBuNRBQTn1eXdx". */
  stripeCustomerId?: string | null
  /** Which Stripe account processed this attempt. */
  stripeAccount?: string | null
  /** Amount drawn for this attempt. Null/absent until something settles. */
  paymentAmount?: number | null
  /**
   * Status of this individual attempt, e.g. `succeeded`. Same open-ended
   * vocabulary as `overallStatus` — the backend owns it.
   */
  paymentStatus?: string | null
  updatedAt?: string | null
  /** Stripe PaymentMethod objects on the customer. First one names the card's bank detail. */
  stripePaymentMethods?: StripePaymentMethodDto[] | null
  defaultStripePaymentMethodId?: string | null
  /** Description set on the client's Stripe billing profile, if any. */
  stripeBillingProfileDescription?: string | null
  /** Stripe's error message for this attempt. Set only when `paymentStatus` is `failed`. */
  stripePaymentErrorMessage?: string | null
  /** Stripe webhook events for this attempt, newest first. */
  stripePaymentEvents?: StripePaymentEventDto[] | null
}

/** Body for POST /funding-invoice/stripe/customers/:stripeCustomerId/payment-intents */
export interface CreateStripePaymentIntentRequestDto {
  /** Integer, in cents (Stripe's convention) — e.g. $61.40 is 6140. */
  amount: number
  /** Billing period this draw applies to, e.g. "2026-07-01". */
  billingPeriod: string
}

/** Response from POST /funding-invoice/stripe/customers/:stripeCustomerId/payment-intents */
export interface CreateStripePaymentIntentResponseDto {
  /** Stripe PaymentIntent id, e.g. "pi_...". */
  paymentIntentId: string
  /** Which Stripe account the intent was created under. */
  stripeAccount: string
  stripeCustomerId: string
  /** Stripe PaymentIntent status, e.g. `succeeded`. */
  status: string
  /** Amount drawn, in cents. */
  amount: number
  /** ISO 4217 currency code. */
  currency: string
  description: string
  /** First day of the funded billing month, e.g. "2026-07-01". */
  billingPeriodStart: string
  /** Stripe payment method charged, e.g. "pm_...". */
  paymentMethod: string
  clientPublicKey: string | null
}

/** Body for POST /funding-invoice/stripe/payment-intents/:paymentIntentId/retry */
export interface RetryStripePaymentIntentRequestDto {
  /** Stripe payment method to charge on retry, e.g. "pm_...". */
  paymentMethod: string
}

/** Settled payment totals for a delivery, as returned on each delivery. */
export interface InvoicePaymentDetailDto {
  /**
   * Sum of the payments that actually succeeded for this invoice — the "actual
   * amount" against the invoiced `invoiceMetadata.totalDue`. Null/absent until
   * something has settled.
   */
  totalSucceededAmount?: number | null
  /**
   * Where the draw stands overall, e.g. `succeeded`. Left as a string rather
   * than a union: the backend owns this vocabulary, and an unrecognised value
   * must still render (title-cased) instead of being swallowed.
   */
  overallStatus?: string | null
  /** Every draw attempt made for this invoice, used to show a succeeded/total ratio. */
  paymentList?: InvoicePaymentAttemptDto[] | null
}

export interface InvoiceDeliveryItemDto {
  id: string
  billingPeriodStart: string
  clientId: string | null
  clientName: string | null
  s3Key: string
  fileName: string
  /** Version number within the (billingPeriodStart, clientId, invoiceTemplate) thread. Starts at 1. */
  version: number
  /** Email template this invoice is sent with. Rows from before templates are MONTHLY. */
  invoiceTemplate: InvoiceTemplate
  /** True when this is the active/current row; older versions are read-only history. */
  isLatest: boolean
  /** When true the delivery is hidden from end users. */
  isArchived: boolean
  status: InvoiceDeliveryStatus
  /** Presigned file URL. Left out for users who may not download invoices (EMS-10939). */
  downloadUrl?: string
  /** Person id. Render `createdByName` / `createdByEmail` instead. */
  createdBy: string
  /** Resolved from `createdBy`. Null when the uploader has no name on record. */
  createdByName: string | null
  /** Resolved from `createdBy`. Null when the uploader has no address. */
  createdByEmail: string | null
  createdAt: string
  /**
   * Due date for this invoice as `YYYY-MM-DD`. Seeded on upload from the
   * client's due day, in the month before the billing period, and overridable
   * per invoice, so it is what the emailed invoice shows. Null when the client
   * has no due day, or the row predates the field.
   */
  dueDate: string | null
  invoiceMetadata?: InvoiceMetadata | null
  /**
   * Cells the parser couldn't convert that still need fixing. Null when there
   * is nothing to act on: the backend omits warnings whose field has since been
   * given a value, so a non-empty list always means the row is actionable.
   */
  parseWarnings?: ParseWarning[] | null
  emailStats?: { delivered: number; total: number } | null
  hasBillingProfile?: boolean
  /**
   * Payment type from the client's billing profile, denormalised onto the
   * delivery by GET /funding-invoice/delivery. Null when the client has no
   * profile, or the company is unmatched.
   */
  paymentType?: InvoicePaymentType | null
  /**
   * The billing profile's own payment type, before template rules (OE1 and new
   * customer OE are always wired). Lets the OE1 split name the OE2 method.
   */
  profilePaymentType?: InvoicePaymentType | null
  /** Settled payment totals. Null when nothing has been drawn for this invoice. */
  paymentDetail?: InvoicePaymentDetailDto | null
  /**
   * What the send that produced this response did. Present only on the send
   * response, not when listing deliveries: `sent` went to every configured
   * recipient, `resent` only to those who had not received it.
   */
  sendOutcome?: 'sent' | 'resent'
  /** Addresses a resend contacted. Empty for a full send. */
  sentTo?: string[]
}

export interface UpdateInvoiceDeliveryMetadataRequestDto {
  /** Pass null to clear, or a finite number to set. */
  totalDue: number | null
}

/** An OE1's Open Enrollment split. Amounts are dollars; null clears. */
export interface UpdateInvoiceDeliveryOeSplitRequestDto {
  totalDue: number | null
  /** `YYYY-MM-DD`. Part 1's wire due date. */
  dueDate: string
  balanceDue: number | null
  oeTotalDue: number | null
}

export interface UpdateInvoiceDeliveryTemplateRequestDto {
  invoiceTemplate: InvoiceTemplate
}

export interface UpdateInvoiceDeliveryDueDateRequestDto {
  /** `YYYY-MM-DD`. Every invoice carries a due date, so it cannot be cleared. */
  dueDate: string
}

export interface UploadSummaryDto {
  total: number
  uploaded: number
  failed: number
}

export interface UploadResultItemDto {
  fileName: string
  result: 'uploaded' | 'failed'
  reason?: string
  delivery?: InvoiceDeliveryItemDto
}

export interface UploadInvoiceFilesResponseDto {
  billingPeriodStart: string
  summary: UploadSummaryDto
  results: UploadResultItemDto[]
}

export interface GetDeliveriesRequestDto {
  billingPeriodStart: string
  /** Filter by delivery status — passed to the backend */
  status?: InvoiceDeliveryStatus
  /** Filter by email template, passed to the backend */
  invoiceTemplate?: InvoiceTemplate
  /** Search by company name or filename — passed to the backend */
  search?: string
  /** Filter by archived state. Omit to return all; false = non-archived only; true = archived only. */
  isArchived?: boolean
  /** Filter by match state. Omit to return all; true = matched only; false = unmatched only. */
  isMatched?: boolean
  /** When true, deliveries belonging to test clients are excluded */
  excludeTestClients?: boolean
}

export interface InvoiceDeliveryListResponseDto {
  billingPeriodStart: string
  deliveries: InvoiceDeliveryItemDto[]
}

export interface BulkSendDeliveryRequestDto {
  ids: string[]
}

/**
 * Scope for a bulk ZIP download. Supply exactly one of `billingPeriodStart`
 * (every latest, matched, non-archived delivery in that month) or `ids` (those
 * specific rows, taken as-is) — the backend rejects neither and both.
 */
export interface DownloadInvoiceArchiveRequestDto {
  billingPeriodStart?: string
  ids?: string[]
  excludeTestClients?: boolean
}

/**
 * What bulk send did with one delivery.
 * - `sent` — first dispatch, went to every configured recipient
 * - `resent` — previously sent, went only to recipients who had not received it
 * - `skipped` — nothing to do (already fully delivered, or nothing retryable)
 * - `failed` — dispatch was attempted and errored
 */
export type BulkSendOutcome = 'sent' | 'resent' | 'skipped' | 'failed'

export interface BulkSendResultDto {
  deliveryId: string
  outcome: BulkSendOutcome
  /** Addresses contacted. Empty for skipped and failed deliveries. */
  recipients: string[]
  /** Why the delivery was skipped or failed. */
  reason?: string
}

export interface BulkUpdateDeliveryStatusResponseDto {
  deliveries: InvoiceDeliveryItemDto[]
  /**
   * Per-delivery outcome. Bulk send decides per row whether to do a full send
   * or a targeted resend to undelivered recipients only, and skips rows with
   * nothing to do — so a single response can mix all four outcomes.
   * Absent from older backends; treat as optional.
   */
  results?: BulkSendResultDto[]
  sentCount?: number
  resentCount?: number
  skippedCount?: number
  failedCount?: number
}

export interface BulkSyncSendGridResponseDto {
  syncedCount: number
  failedCount: number
  skippedCount: number
  /** SendGrid HTTP errors (network / 4xx / 5xx) — these deliveries were skipped, not "no events yet". */
  sgErrorCount: number
  /**
   * Latest SENDING deliveries auto-failed because SendGrid had no record of them
   * past the stuck-delivery threshold (currently 30 min). The send never made it
   * out — operator should investigate the original send path, then re-upload.
   */
  stuckAutoFailedCount: number
}

export interface DeleteDeliveryResponseDto {
  id: string
}

export interface ClientOptionDto {
  id: string
  name: string
  /** Set when the list was requested for a billing period. */
  profileDueDate?: string | null
  profilePaymentType?: InvoicePaymentType | null
}

/** Values typed in the upload review; the server fills the rest from the billing profile. */
export interface PreviewDraftEmailRequestDto {
  invoiceTemplate: InvoiceTemplate
  billingPeriodStart: string
  clientId?: string
  companyName?: string
  amount?: number
  dueDate?: string
  balanceDue?: number
}

/** An email rendered from its active SendGrid template. */
export interface InvoiceEmailPreviewDto {
  subject: string
  html: string
}

export interface GetClientsResponseDto {
  clients: ClientOptionDto[]
}

export interface MatchDeliveryRequestDto {
  clientId: string
  clientName: string
}

export interface SetDeliveryArchivedRequestDto {
  isArchived: boolean
}

export interface InvoiceDeliveryStatusCountsDto {
  total: number
  draft: number
  sending: number
  delivered: number
  failed: number
  /** Number of deliveries that could not be auto-matched to a known client. */
  unmatched: number
  /** Number of listed deliveries with at least one unresolved parse warning. */
  withWarnings: number
}

export interface GetDeliveryStatsRequestDto {
  billingPeriodStart: string
  excludeTestClients?: boolean
  /**
   * Filter by archived state. Must mirror the value sent to `getDeliveries`,
   * otherwise the counts describe rows the table never lists.
   */
  isArchived?: boolean
}

export interface InvoiceDeliveryStatsResponseDto {
  billingPeriodStart: string
  counts: InvoiceDeliveryStatusCountsDto
}

export interface InvoiceDeliveryHistoryResponseDto {
  billingPeriodStart: string
  clientId: string | null
  clientName: string | null
  /** All versions for this (period, clientId) thread, newest first. */
  versions: InvoiceDeliveryItemDto[]
}

export interface EmailRecipientResultDto {
  email: string
  role: 'to' | 'cc'
  event: string
  reason?: string
  timestamp: number
}

export interface EmailDeliveryAttemptDto {
  id: string
  createdAt: string
  recipients: EmailRecipientResultDto[]
  /**
   * True when a dispatch snapshot was captured for this attempt. Drives
   * whether the "View sent email" link appears in the email-results drawer.
   * Absent on legacy attempts written before snapshot capture was added.
   */
  hasPreview?: boolean
}

export interface InvoiceDeliveryEmailLogsResponseDto {
  deliveryId: string
  /** Email delivery attempts, oldest first */
  attempts: EmailDeliveryAttemptDto[]
}

export interface InvoiceDeliveryGlobalSettingsDto {
  ccEmails: string[]
  updatedAt: string | null
  updatedBy: string | null
}

export interface UpdateInvoiceDeliveryGlobalSettingsDto {
  ccEmails: string[]
}

/**
 * Current state of one recipient address, collapsed by the backend across
 * every send attempt. `no_response` means the address was dispatched to but no
 * webhook has arrived — indistinguishable from "about to arrive", so it is
 * never treated as a failure.
 */
export type RecipientOutcome =
  | 'delivered'
  | 'bounced'
  | 'dropped'
  | 'blocked'
  | 'unsubscribed'
  | 'spam_reported'
  | 'no_response'

export interface SendPreviewRecipientDto {
  email: string
  role: 'to' | 'cc'
  outcome: RecipientOutcome
  /** Bounce, drop or block reason from SendGrid. */
  reason?: string
  /** Unix timestamp of the event behind this outcome. */
  timestamp?: number
  /**
   * Whether this recipient is ticked when the modal opens. The server decides
   * it — true only when the address has not received the invoice — so that
   * emailing a duplicate is always a deliberate click.
   */
  defaultSelected: boolean
  /**
   * Whether any prior attempt actually emailed this address. False for one
   * added to the billing profile or global CC list after the invoice went out:
   * it has no events, so its outcome is `no_response`, but nothing was ever
   * sent to it. Distinguishes "emailed, awaiting confirmation" from "never
   * emailed" — the two would otherwise look identical.
   */
  wasDispatched: boolean
}

export interface SendPreviewDeliveryDto {
  deliveryId: string
  clientName?: string
  fileName: string
  status: InvoiceDeliveryStatus
  version: number
  sentAt?: string
  recipients: SendPreviewRecipientDto[]
  defaultSelected: boolean
  /**
   * Why this delivery cannot be sent at all. Present means the row is shown
   * but not selectable — blocked rows are returned rather than omitted so the
   * modal's row count always matches the table's selection.
   */
  blockedReason?: string
}

export interface SendPreviewRequestDto {
  ids: string[]
}

export interface SendPreviewResponseDto {
  deliveries: SendPreviewDeliveryDto[]
}

export interface SendSelectedDeliveryDto {
  deliveryId: string
  /** Exact addresses to email. Server rejects any it cannot attribute. */
  recipients: string[]
}

export interface SendSelectedRequestDto {
  deliveries: SendSelectedDeliveryDto[]
}
